import React, { FC, MouseEventHandler } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import set from 'lodash/set';
import styled, { css } from 'styled-components';

import Form, { useForm } from 'src/presentation/components/forms/Form';
import PhoneNumberInput from 'src/presentation/components/forms/PhoneNumberInput';
import PopoverMenu, { usePopoverMenu } from '@ad-scripts/presentation/components/common/menu';
import ivrResource from 'src/actions/ivrs/ivrResource';
import validatePhoneNumber from 'src/presentation/components/pages/ivr/helpers/phoneNumberValidator';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { RootState } from 'src/actions/configuration/rootReducer';

type PropsType = {
  isDisabled: boolean;
};

const PhoneCallPopover: FC<PropsType> = ({ isDisabled }) => {
  const { validate } = ivrResource.effects;
  const { resetErrors } = ivrResource.actions;

  const dispatch = useDispatch();
  const errors = useSelector((state: RootState) => state.ivr.errors);
  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();
  const initializedItem = { phoneNumber: '' };
  const aidPrefix = 'phone-call-popover';

  const handleButtonSave: MouseEventHandler<HTMLButtonElement> = () => {
    const { phoneNumber } = workingState;
    const isValid = !!dispatch(validatePhoneNumber(phoneNumber));

    if (isValid) {
      dispatch(ivrResource.effects.makePhoneCall(workingState.phoneNumber));
      handleModalClose();
    }
  };

  const handleModalClose = (): void => {
    const hasError = errors.find(err => err.attr === 'phoneNumber');
    hasError && resetError({ attr: 'phoneNumber', errors });
    updateState(set({}, 'phoneNumber', ''));
    onClose();
  };

  const formContext = useForm({
    errors,
    item: initializedItem,
    onSave: handleButtonSave,
    resetErrors,
    validate,
  });

  const { onSave, workingState, resetError, updateState } = formContext;

  const getContent = (): JSX.Element => (
    <>
      <StyledDescription>
        Enter your phone number and Gladly will call you from a random phone number to test your configuration.
        <P>
          <StyledFontBold>Note:</StyledFontBold> Inbox routing, hold music, and your queue IVR may not function as
          expected during testing.
        </P>
      </StyledDescription>
      <Form context={formContext} preventSaveOnEnter>
        <PhoneNumberInput data-aid={`${aidPrefix}-input`} attr="phoneNumber" label="Phone Number" />
      </Form>
      <StyledControl>
        <StyledButtonCancel data-aid={`${aidPrefix}-btn-cancel`} type={Button.Types.TEXT} onClick={handleModalClose}>
          Cancel
        </StyledButtonCancel>
        <Button onClick={onSave} data-aid={`${aidPrefix}-btn-ok`}>
          OK
        </Button>
      </StyledControl>
    </>
  );

  return (
    <>
      <Button
        data-aid={`${aidPrefix}-btn-open`}
        type={Button.Types.SECONDARY}
        onClick={onToggle}
        ref={setTargetRef}
        disabled={isDisabled}
      >
        Test
      </Button>
      <StyledPopoverMenu
        data-aid={`${aidPrefix}-content`}
        isOpen={isOpen}
        onClose={handleModalClose}
        targetRef={targetRef}
      >
        {getContent()}
      </StyledPopoverMenu>
    </>
  );
};

const cssTriangle = css`
  &::before {
    content: '';
    display: block;
    position: absolute;
    top: 0%;
    left: 50%;
    width: 0;
    height: 0;
    transform: translate(-50%, -100%);
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-bottom: 10px solid ${({ theme }) => theme.colors.white};
  }
`;

const StyledPopoverMenu = styled(PopoverMenu)`
  border-radius: ${({ theme }) => theme.borderRadius.default};
  padding: ${({ theme }) => theme.spacing.insetLarge};
  padding-bottom: ${({ theme }) => theme.spacing.insetSmall};
  overflow: visible;
  max-width: 265px;
  box-sizing: border-box;
  ${cssTriangle}
`;

const StyledDescription = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.stackLarge};
`;

const StyledControl = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.medium};
`;

const StyledButtonCancel = styled(Button)`
  padding: ${({ theme }) => theme.spacing.small};
  margin-right: ${({ theme }) => theme.spacing.large};
`;

const P = styled.p`
  margin-bottom: 0;
`;

const StyledFontBold = styled.span`
  font-weight: bold;
`;

export default PhoneCallPopover;
